export const c408Config = {
  slug: "c408",
  code: "PROG-C408-24",
  category: "Type · Cessna 408",
  name: "Cessna 408 SkyCourier",
  insurance: true,
  heroImage:
    "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/rlcr17in_IMG_6963.webp",
  line1: "SkyCourier",
  line2Italic: "",
  line2: "Training",
  subtitle:
    "El Elyon Air provides in-aircraft initial and recurrent C408 training approved through W. Brown & Associates — the leading aviation insurance underwriter for King Air and turbine aircraft operators. Ground school, systems, procedures, and scenario-based flight training built by working captains, for working captains.",
  overview: [
    "El Elyon Air is insurance-approved for in-aircraft C408 SkyCourier initial and recurrent training through W. Brown & Associates, the leading aviation insurance underwriter for King Air and turbine aircraft operators. That approval matters: it means your policy stays in force while you train in the real airplane with our instructors, not just in a simulator.",
    "The Cessna 408 Sky Courier is a large-cabin, high-wing twin-turboprop designed for demanding regional missions — 6,000 lb of payload, quick-change cargo and pax configurations, and a flight deck built around the Garmin G1000 NXi.",
    "Our program treats the airplane the way it deserves to be treated: as a purpose-built freighter that rewards discipline. We teach the flows, the numbers, and the judgment calls that separate a checkride pass from a captain the ramp trusts.",
    "Every module is written and audited by pilots currently flying the type. No slide-deck theatrics — just the airplane, the mission, and what it takes to fly it well on a Tuesday morning in real weather.",
  ],
  specs: [
    { label: "Engines", value: "PT6A-65SC", unit: "Twin turboprop" },
    { label: "Max Payload", value: "6,000", unit: "Pounds" },
    { label: "Cruise", value: "200", unit: "KTAS" },
    { label: "Flight Deck", value: "G1000", unit: "NXi" },
  ],
  curriculumIntro:
    "Twelve modules across ground and flight training. Structured for both first-time turboprop captains transitioning up and seasoned operators moving onto type.",
  modules: [
    {
      title: "Airframe & Systems",
      body:
        "High-wing structure, cargo door and pallet system, fuel system architecture, environmental and pressurization overview, electrical bus philosophy.",
    },
    {
      title: "Powerplant · PT6A-65SC",
      body:
        "Free-turbine principles, start sequence, torque and ITT management, propeller governing, feathering behavior, and hot-start prevention.",
    },
    {
      title: "G1000 NXi Integration",
      body:
        "PFD/MFD flows, FMS programming for cargo profiles, ADS-B In/Out interpretation, SVT usage in low-visibility ops, and CAS logic.",
    },
    {
      title: "Normal Procedures & Flows",
      body:
        "Cockpit setup, taxi and takeoff flows, cruise checks, descent-and-approach flow, after-landing and shutdown — memorized and drilled cold.",
    },
    {
      title: "Abnormal & Emergency Procedures",
      body:
        "Engine failure at V1 and airborne, pressurization loss, electrical failures, smoke/fire scenarios, and emergency descent profile.",
    },
    {
      title: "Performance & Weight-and-Balance",
      body:
        "Runway performance, single-engine climb, cargo loading templates, CG envelopes for common freight configurations.",
    },
    {
      title: "Weather & Route Strategy",
      body:
        "Icing envelope, deice/anti-ice systems, thunderstorm avoidance for turboprop cruise altitudes, mountain-wave awareness.",
    },
    {
      title: "Crew Resource Management",
      body:
        "Two-crew flows, threat-and-error management, sterile cockpit discipline, callouts, and captain-first-officer role clarity.",
    },
    {
      title: "Single-Pilot Techniques",
      body:
        "For operators approved single-pilot: workload shedding, autoflight strategy, hand-flying discipline, and go-around protection.",
    },
    {
      title: "Cargo & Passenger Ops",
      body:
        "Quick-change procedures, restraint standards, dangerous-goods handling overview, and passenger-briefing standards.",
    },
    {
      title: "Simulator Scenarios",
      body:
        "Full LOFT (line-oriented flight training) sessions built around real regional-cargo missions with induced failures and weather.",
    },
    {
      title: "Checkride Preparation",
      body:
        "Oral prep, published ACS standards, checkride flow, and a full mock ride with a designated examiner where available.",
    },
  ],
  audience: [
    "Regional cargo operators onboarding new-hire or upgrade captains to the C408.",
    "Owner-operators taking delivery of a Sky Courier and building initial mission proficiency.",
    "Captains transitioning from Caravan or King Air platforms into the larger 408 airframe.",
    "Recurrent-training pilots seeking a program that respects experience while raising standards.",
  ],
};

export const kingAirConfig = {
  slug: "king-air",
  code: "PROG-KA-24",
  category: "Type · Beechcraft King Air",
  name: "Beechcraft King Air",
  heroImage:
    "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/345d7k6y_IMG_7905.webp",
  line1: "King Air",
  line2Italic: "",
  line2: "Training",
  subtitle:
    "Initial and recurrent training for the Beechcraft King Air 200 and 350 families — corporate aviation's most-flown turboprop. Systems, flows, single-pilot IFR discipline, and the judgment that owner-operated flying demands.",
  overview: [
    "The King Air line — the 200, B200, 250, 300, and 350 — has been the backbone of corporate turboprop flying for four decades. Our program is written for pilots who intend to fly it seriously, not casually.",
    "We build King Air captains around a simple idea: the airplane is generous, but the mission won't be. Our modules focus on the details that matter when weather closes in, when a passenger is watching, and when you are the only pilot on the flight deck.",
    "Instructors are current King Air captains and CFIIs who fly the airplane in the same environments our students will — corporate, contract, and owner-flown. Every scenario has a source in real ops.",
  ],
  specs: [
    { label: "Engines", value: "PT6A", unit: "-42 / -60A / -67D" },
    { label: "Seating", value: "6 – 11", unit: "Executive · Utility" },
    { label: "Cruise", value: "260 – 312", unit: "KTAS" },
    { label: "Ceiling", value: "35,000", unit: "Feet" },
  ],
  curriculumIntro:
    "Ten integrated modules covering ground school, procedures, single-pilot IFR, and scenario-based sim work. Structured for 200/250/300/350 variants — instructor selects module depth to match your target airframe.",
  modules: [
    {
      title: "Airframe & Family Differences",
      body:
        "Structural and system differences across 200/B200/250/300/350 variants — what carries over and what catches pilots on transition.",
    },
    {
      title: "Powerplant · PT6A Series",
      body:
        "Free-turbine architecture, torque limits, ITT/N1 management, propeller behavior, autofeather logic, and start abnormalities.",
    },
    {
      title: "Avionics · Pro Line Fusion & G1000",
      body:
        "Flight-deck flows for the two most common avionics suites, FMS programming, autoflight modes, and coupled-approach behavior.",
    },
    {
      title: "Normal Procedures & Flows",
      body:
        "Cockpit setup, engine start, taxi and takeoff flows, cruise checks, and single-pilot descent/approach discipline.",
    },
    {
      title: "Abnormal & Emergency Procedures",
      body:
        "Engine failure profiles, pressurization emergencies, electrical management, gear malfunctions, and emergency descent.",
    },
    {
      title: "Single-Pilot IFR Discipline",
      body:
        "Workload management, autoflight strategy, en-route decision points, approach briefings, and go-around triggers.",
    },
    {
      title: "Performance & Weight-and-Balance",
      body:
        "Field performance, single-engine climb, cabin loading for family and passenger ops, CG considerations at aft-loaded config.",
    },
    {
      title: "Weather & High-Altitude Ops",
      body:
        "Icing certification limits, TKS or boot management, upper-atmosphere weather, and cabin-altitude planning.",
    },
    {
      title: "Owner-Flown Judgment",
      body:
        "Personal minimums, mission-vs-schedule pressure, and the quiet discipline that separates safe owner-operators from statistics.",
    },
    {
      title: "Simulator Scenarios & Checkride Prep",
      body:
        "Full LOFT scenarios, published ACS/ATP standards review, oral prep, and mock rides with a designated examiner where available.",
    },
  ],
  audience: [
    "Owner-operators transitioning up from piston twins into the King Air family.",
    "Corporate flight departments running 200, 250, 300, or 350 aircraft needing initial or recurrent training.",
    "Contract captains and first officers building type-current experience.",
    "Pilots preparing for ATP or single-pilot type-checkride environments.",
  ],
};
